import {
  clearAuthSession,
  clearTenantSession,
  dispatchAuthSessionExpired,
  getPersistLoginPreference,
  getStoredAuthSession,
  getTenantStoredUser,
  saveAuthSession,
} from '@saasplatform/auth';
import type { AuthResponseLike } from '@saasplatform/auth';
import { getApiBaseUrl, getTenantApiBaseUrl, isTenantRuntime } from '@saasplatform/config';
import { ApiError } from './errors';

interface RequestConfig {
  baseURL?: string;
  url?: string;
  params?: object;
  headers?: Record<string, string>;
  data?: unknown;
  credentials?: RequestCredentials;
  timeout?: number;
  _retry?: boolean;
}

interface SuccessResponse<T = unknown> {
  data: T;
  status: number;
  headers: Headers;
}

interface ErrorResponsePayload {
  message?: string | string[];
  code?: string;
  [key: string]: unknown;
}

const DEFAULT_TIMEOUT_MS = 10000;
const DEFAULT_TENANT_TIMEOUT_MS = 60000;

const isTenantRequestContext = (config?: { baseURL?: string; url?: string }) => {
  if (typeof window !== 'undefined' && /^\/tenant(\/|$)/.test(window.location.pathname)) {
    return true;
  }

  const baseURL = config?.baseURL || '';
  return baseURL.includes('3012') || (typeof window !== 'undefined' && isTenantRuntime());
};

const shouldSkipRefresh = (url?: string) => {
  return Boolean(url && (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/register')));
};

const canAttemptAdminRefresh = () => Boolean(getStoredAuthSession()?.user);

const appendQueryParams = (url: URL, params?: object) => {
  if (!params) {
    return;
  }

  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          url.searchParams.append(key, String(item));
        }
      });
      return;
    }

    if (typeof value === 'object') {
      url.searchParams.append(key, JSON.stringify(value));
      return;
    }

    url.searchParams.append(key, String(value));
  });
};

const buildUrl = (path: string, config?: RequestConfig) => {
  const configuredBaseURL = config?.baseURL || getApiBaseUrl();
  const resolvedBaseURL =
    configuredBaseURL && /^https?:\/\//i.test(configuredBaseURL)
      ? configuredBaseURL
      : typeof window !== 'undefined'
        ? new URL(configuredBaseURL || '/', window.location.origin).toString()
        : configuredBaseURL;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  const base = new URL(resolvedBaseURL);
  const basePathname = base.pathname.endsWith('/') ? base.pathname : `${base.pathname}/`;
  const url = new URL(normalizedPath, `${base.origin}${basePathname}`);
  appendQueryParams(url, config?.params);
  return url.toString();
};

const parseResponseBody = async (response: Response) => {
  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  return text;
};

let refreshPromise: Promise<string | null> | null = null;

const refreshAccessToken = async (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await sendRequest<{
        accessToken?: string;
        refreshToken?: string;
        user: unknown;
      }>('/auth/refresh', 'POST', undefined, { _retry: true });

      saveAuthSession(response.data as AuthResponseLike, {
        persistLogin: getPersistLoginPreference(),
      });

      return response.data.accessToken || null;
    })()
      .catch(() => {
        clearAuthSession();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

const normalizeMessage = (message: unknown, fallback: string) => {
  if (Array.isArray(message)) {
    return message.filter(Boolean).join('；') || fallback;
  }

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallback;
};

const sendRequest = async <T>(
  path: string,
  method: string,
  body?: unknown,
  config?: RequestConfig,
): Promise<SuccessResponse<T>> => {
  const controller = new AbortController();
  const resolvedTimeout =
    config?.timeout ??
    (isTenantRequestContext({ baseURL: config?.baseURL, url: path })
      ? DEFAULT_TENANT_TIMEOUT_MS
      : DEFAULT_TIMEOUT_MS);
  const timeoutId = window.setTimeout(() => controller.abort(), resolvedTimeout);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(config?.headers || {}),
    };

    const response = await fetch(buildUrl(path, config), {
      method,
      credentials: config?.credentials ?? 'include',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });

    const parsed = await parseResponseBody(response);

    if (!response.ok) {
      if (
        response.status === 401 &&
        !isTenantRequestContext(config) &&
        !shouldSkipRefresh(path) &&
        canAttemptAdminRefresh() &&
        !config?._retry
      ) {
        const nextAccessToken = await refreshAccessToken();
        if (nextAccessToken) {
          return sendRequest<T>(path, method, body, {
            ...config,
            _retry: true,
          });
        }
      }

      if (response.status === 401) {
        const isTenant = isTenantRuntime();
        const hadStoredSession = isTenant
          ? Boolean(getTenantStoredUser())
          : Boolean(getStoredAuthSession()?.user);
        if (isTenant) {
          clearTenantSession();
        } else {
          clearAuthSession();
        }
        if (hadStoredSession) {
          dispatchAuthSessionExpired({ tenant: isTenant });
        }
      }

      const errorPayload = (parsed || {}) as ErrorResponsePayload;
      throw new ApiError(normalizeMessage(errorPayload.message, '请求失败'), {
        status: response.status,
        code: errorPayload.code,
        details: errorPayload,
        requestId: response.headers.get('x-request-id') || undefined,
      });
    }

    return {
      data: parsed as T,
      status: response.status,
      headers: response.headers,
    };
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('请求超时，请稍后重试', {
        isNetworkError: true,
      });
    }

    throw new ApiError('网络错误，请检查您的网络连接', {
      isNetworkError: true,
      details: error,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const unwrap = async <T>(promise: Promise<SuccessResponse<T>>) => {
  const response = await promise;
  return response.data;
};

const request = {
  get: <T = unknown>(path: string, config?: RequestConfig) => unwrap(sendRequest<T>(path, 'GET', undefined, config)),
  post: <T = unknown>(path: string, body?: unknown, config?: RequestConfig) => unwrap(sendRequest<T>(path, 'POST', body ?? config?.data, config)),
  patch: <T = unknown>(path: string, body?: unknown, config?: RequestConfig) => unwrap(sendRequest<T>(path, 'PATCH', body ?? config?.data, config)),
  put: <T = unknown>(path: string, body?: unknown, config?: RequestConfig) => unwrap(sendRequest<T>(path, 'PUT', body ?? config?.data, config)),
  delete: <T = unknown>(path: string, config?: RequestConfig) => unwrap(sendRequest<T>(path, 'DELETE', config?.data, config)),
};

export { getTenantApiBaseUrl };
export default request;
